import { useCallback, useEffect, useState } from 'react';
import CancelWithCircle from '@/icons/cancel-with-circle';
import { Button } from '@/shadcn-ui/button';
import { Dialog, DialogContent } from '@/shadcn-ui/dialog';
import ImageGalleryCarousel from './image-gallery-carousel';
import ImageGalleryScroll from './image-gallery-scroll';
import { VehicleImageWithMeta } from '@/catalog/types/vehicle';

const findDefaultImageIndex = (
  images: VehicleImageWithMeta[],
  selectedImage?: string
) => {
  // Filter out Exterior_0 images
  const filteredImages = images.filter((img) => img.image_type !== 'Exterior_0');

  if (!selectedImage) return 0;
  const index = filteredImages.findIndex((image) => selectedImage.includes(image.path));
  return index >= 0 ? index : 0;
};

interface ImageGalleryProps {
  open: boolean;
  /** Images still needed at this level to track scrolling to elements user clicked on */
  images: VehicleImageWithMeta[];
  interiorImages: VehicleImageWithMeta[];
  exteriorImages: VehicleImageWithMeta[];
  cdnUrl: string;
  selectedImage?: string;
  onCloseDelegate: () => void;
}

export default function ImageGallery({
  open,
  images,
  interiorImages,
  exteriorImages,
  cdnUrl,
  selectedImage,
  onCloseDelegate,
}: ImageGalleryProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(
    findDefaultImageIndex(images, selectedImage)
  );

  const goToPrevious = useCallback(() => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  const goToNext = useCallback(() => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseDelegate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseDelegate]);

  // Update the selected image to whichever thumbnail the user selected from the image previews.
  useEffect(() => {
    setSelectedImageIndex(findDefaultImageIndex(images, selectedImage));
  }, [images, selectedImage]);

  return (
    <Dialog open={open} onOpenChange={onCloseDelegate}>
      <DialogContent
        className="max-h-[100dvh] max-w-[100dvw] rounded-none border-none bg-flexcar-neutral10 p-0"
        overlayClassName="py-0"
      >
        <div className="relative flex h-[100dvh] w-[100dvw] flex-col px-0 py-0 lg:px-[24px] lg:pb-[40px] lg:pt-[80px] xl:px-[40px]">
          <Button
            variant="clear"
            onClick={() => onCloseDelegate()}
            className="absolute right-[28px] top-[28px] z-10 h-fit focus-visible:outline-none"
          >
            <CancelWithCircle className="focus:outline-none focus-visible:outline-none" />
          </Button>

          <div className="block h-full lg:hidden">
            <ImageGalleryScroll
              images={images}
              interiorImages={interiorImages}
              exteriorImages={exteriorImages}
              selectedImageIndex={selectedImageIndex}
              cdnUrl={cdnUrl}
            />
          </div>

          <div className="hidden h-full lg:block">
            <ImageGalleryCarousel
              images={images}
              interiorImages={interiorImages}
              exteriorImages={exteriorImages}
              selectedImageIndex={selectedImageIndex}
              setSelectedImageIndex={setSelectedImageIndex}
              goToPrevious={goToPrevious}
              goToNext={goToNext}
              cdnUrl={cdnUrl}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
